// Screen flow: landing (register / sign in) → character creator → the club.
// Each step resolves when its server-side fact exists (session cookie,
// character row); refresh at any point resumes from the right screen.

import { startGame } from "./game";
import { bakedBytes, loadBakedAssets } from "./render/assets";
import { authoredBytes, preloadAuthoredAssets } from "./render/authored-assets";
import { characterBytes, loadCharacter } from "./render/character";
import { loadPortKit } from "./render/port-kit";
import { showCreator } from "./ui/creator";
import { showLanding } from "./ui/landing";
import "./style.css";

interface MeResponse {
  user: { email: string };
  character: { name: string } | null;
}

async function fetchMe(): Promise<MeResponse | undefined> {
  const res = await fetch("/api/me");
  if (res.status === 401) return undefined;
  if (!res.ok) throw new Error(`/api/me failed: ${res.status}`);
  return (await res.json()) as MeResponse;
}

const app = document.getElementById("app");
if (!app) throw new Error("missing #app root");

async function flow(): Promise<void> {
  let me = await fetchMe();
  if (!me) {
    await showLanding(app as HTMLElement);
    me = await fetchMe();
    if (!me) throw new Error("signed in but no session");
  }
  // The gate runs BEFORE the creator, not between it and the game: the
  // creator's preview is now the same baked character the world draws, so it
  // needs the model on the GPU to show anything at all — and that is the
  // point of the preview, which only predicts what you get if it IS what you
  // get.
  await gateOnAssets(app as HTMLElement);
  if (!me.character) {
    await showCreator(app as HTMLElement);
  }
  startGame(app as HTMLElement);
}

/**
 * The asset gate. The shipped material set (tools/assets → KTX2, loaded by
 * render/assets.ts) must be on the GPU before the first scene is built, or a
 * surface whose runtime generator no longer exists renders as nothing.
 *
 * It sits HERE, between the creator and the game, rather than inside
 * startGame(), because startGame builds a renderer and a world and a socket
 * in one synchronous pass and every one of them assumes its textures are
 * already there. A gate is a smaller thing to reason about than a
 * half-initialised game.
 */
async function gateOnAssets(root: HTMLElement): Promise<void> {
  const note = document.createElement("div");
  note.className = "boot-loading";
  note.textContent = "loading materials…";
  root.append(note);
  const started = performance.now();
  try {
    await loadBakedAssets();
    note.textContent = "loading the worlds…";
    await preloadAuthoredAssets();
    // The character is part of the same gate: an avatar that pops in two
    // seconds after the world does is a worse first frame than a load screen
    // two seconds longer, and the creator's preview needs it before it draws.
    note.textContent = "loading the figure…";
    await loadCharacter();
    note.textContent = "loading the freight terminal…";
    await loadPortKit(["freight-container-lod"]);
    // Worth a line in the console: this is the number that replaced the CPU
    // the forge used to spend, and the one that grows as phases 2 and 3 ship
    // meshes and atlases through the same door.
    console.info(
      `[bebop] materials ${(bakedBytes() / 1024).toFixed(0)} kB, character ${(characterBytes() / 1024).toFixed(0)} kB` +
        `, authored ${(authoredBytes() / 1024).toFixed(0)} kB` +
        ` in ${Math.round(performance.now() - started)} ms`,
    );
  } finally {
    note.remove();
  }
}

flow().catch((err) => {
  console.error("[bebop] boot failed", err);
  const div = document.createElement("div");
  div.className = "boot-error";
  div.textContent = "Something broke on the way in — refresh to try again.";
  app?.append(div);
});
